import { AlertTriangle, Check } from 'lucide-react';

import { CHANNEL_LABELS, SCOPE_LABELS } from '@/components/destinations/utils';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Textarea } from '@/components/ui/catalyst/textarea';

import { groupSelectedScopesByDestination } from './threading';
import type { WizardScopeSelection } from './useCreateRequestWizard';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ReviewStepProps {
  selectedScopes: WizardScopeSelection[];
  notes: string;
  onSetNotes: (notes: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ReviewStep = ({
  selectedScopes,
  notes,
  onSetNotes,
}: ReviewStepProps) => {
  const allScopesAreReady = selectedScopes.every(
    (s) => s.destination !== null || s.createWithoutDestination,
  );
  const threadGroups = groupSelectedScopesByDestination(selectedScopes);

  return (
    <div className="space-y-6">
      {/* Summary list */}
      <div>
        <p className="mb-3 text-sm font-medium text-zinc-700">
          Requests to be created ({threadGroups.length})
        </p>
        <div className="space-y-2">
          {threadGroups.map((group, index) => (
            <div
              key={group.key}
              className="flex items-start gap-3 rounded-lg border border-zinc-200 px-4 py-3"
            >
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-xs font-medium text-zinc-600">
                {index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-900">
                  {group.scopes
                    .map((scopeSel) => SCOPE_LABELS[scopeSel.scope])
                    .join(', ')}
                </p>

                {group.destination ? (
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {CHANNEL_LABELS[group.destination.channel_type]}{' '}
                    {group.destination.value}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-amber-700">
                    No destination selected (placeholder request)
                  </p>
                )}

                {group.followUpPhone && (
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Follow-up phone: {group.followUpPhone}
                  </p>
                )}
              </div>

              <Check className="mt-0.5 size-4 shrink-0 text-emerald-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Warning if any scope is missing a destination */}
      {!allScopesAreReady && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <AlertTriangle className="size-4 shrink-0" />
          <span>
            Some scopes are missing configuration. Go back and either select a
            destination or choose placeholder request mode.
          </span>
        </div>
      )}

      {/* Notes */}
      <Field>
        <Label>Notes (optional)</Label>
        <Textarea
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onSetNotes(e.target.value)
          }
          placeholder="Add any notes or special instructions..."
          rows={3}
        />
      </Field>
    </div>
  );
};
